// Клиент `/api/v1/connections/forgejo/tokens/*`.
//
// Token-value никогда не возвращается из GET-эндпоинтов — только
// передаётся в body PUT. JWT прикручивает `authedFetch`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";
import { buildJsonHeaders, ForgejoRequestError, HttpError } from "./api";

export { HttpError, buildJsonHeaders } from "./api";

// Статусы токена, зеркалят backend-домен.
export type TokenStatus =
  | "active"
  | "expiring"
  | "expired"
  | "superseded"
  | "parked"
  | "revoked"
  | "relinquished";

// Режим выпуска токена.
export type IssuanceMode = "user_provided" | "baton_issued";

// Тип привязки токена.
export type BindingKind = "connection" | "role";

// Тристейт возможностей при probe.
export type CapabilityState = "supported" | "unsupported" | "unknown";

export interface RepoHostTokenSummary {
  readonly binding_kind: BindingKind;
  readonly role_name: string | null;
  readonly host_username: string;
  readonly token_name: string;
  readonly declared_scopes: readonly string[];
  readonly access_level: string | null;
  readonly issuance_mode: IssuanceMode;
  readonly status: TokenStatus;
  readonly expires_at: string | null;
  readonly last_rotated_at: string | null;
  readonly last_probed_at: string | null;
  // Интервал плановой ротации в днях; null — «никогда», ротация отключена.
  readonly rotation_interval_days: number | null;
}

export interface RepoHostTokensResponse {
  readonly tokens: readonly RepoHostTokenSummary[];
}

export interface ProbedCapabilitiesResponse {
  readonly host_username: string;
  readonly can_read_repository: CapabilityState;
  readonly can_write_repository: CapabilityState;
  readonly can_read_issue: CapabilityState;
  readonly can_write_issue: CapabilityState;
  readonly can_delete_repository: CapabilityState;
  readonly can_delete_protected_branch: CapabilityState;
  readonly is_admin: CapabilityState;
}

export interface IssuanceCheckResponse {
  readonly can_issue: boolean;
  readonly reason: string;
  readonly manual_guide_available: boolean;
}

export interface ProvisioningGuideResponse {
  readonly bot_username: string;
  readonly token_name: string;
  readonly required_scopes: readonly string[];
  readonly repo_restriction: readonly string[];
  readonly steps: readonly string[];
}

// host_username не отправляется — backend деривит его из GET /user самим
// токеном (спека § «Проверка прав»).
export interface UpdateTokenRequest {
  readonly token_value: string;
  readonly token_name?: string | null;
  readonly declared_scopes?: readonly string[];
}

export interface DeleteTokenResponse {
  readonly revoked: boolean;
  readonly deleted: boolean;
}

// Границы интервала ротации в днях, зеркало `token_mutations.py`.
export const MIN_ROTATION_INTERVAL_DAYS = 1;
export const MAX_ROTATION_INTERVAL_DAYS = 3650;

export interface SetRotationIntervalRequest {
  readonly rotation_interval_days: number | null;
}

const BASE = "/api/v1/connections/forgejo/tokens";

export async function getRepoHostTokens(): Promise<RepoHostTokensResponse> {
  const response = await authedFetch(BASE, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getRepoHostTokens: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RepoHostTokensResponse;
}

export async function probeRepoHostToken(
  role: string,
): Promise<ProbedCapabilitiesResponse> {
  const response = await authedFetch(`${BASE}/${role}/probe`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `probeRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ProbedCapabilitiesResponse;
}

export async function canIssueRepoHostToken(
  role: string,
): Promise<IssuanceCheckResponse> {
  const response = await authedFetch(`${BASE}/${role}/can-issue`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `canIssueRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as IssuanceCheckResponse;
}

export async function getRepoHostTokenGuide(
  role: string,
): Promise<ProvisioningGuideResponse> {
  const response = await authedFetch(`${BASE}/${role}/guide`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getRepoHostTokenGuide: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ProvisioningGuideResponse;
}

export async function updateRepoHostToken(
  role: string,
  body: UpdateTokenRequest,
): Promise<RepoHostTokenSummary> {
  const response = await authedFetch(`${BASE}/${role}`, {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `updateRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RepoHostTokenSummary;
}

export async function deleteRepoHostToken(
  role: string,
): Promise<DeleteTokenResponse> {
  const response = await authedFetch(`${BASE}/${role}`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `deleteRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as DeleteTokenResponse;
}

export async function issueRepoHostToken(
  role: string,
): Promise<RepoHostTokenSummary> {
  const response = await authedFetch(`${BASE}/${role}/issue`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `issueRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RepoHostTokenSummary;
}

export async function rotateRepoHostToken(
  role: string,
): Promise<RepoHostTokenSummary> {
  const response = await authedFetch(`${BASE}/${role}/rotate`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `rotateRepoHostToken: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RepoHostTokenSummary;
}

export async function setRepoHostTokenRotationInterval(
  role: string,
  days: number | null,
): Promise<RepoHostTokenSummary> {
  const body: SetRotationIntervalRequest = { rotation_interval_days: days };
  const response = await authedFetch(`${BASE}/${role}/rotation-interval`, {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `setRepoHostTokenRotationInterval: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RepoHostTokenSummary;
}

// Тело POST /bot-account — привязка существующего бота по Basic Auth.
// password идёт ТОЛЬКО в request body (инвариант: не в URL/логах/store).
export interface BindBotAccountRequest {
  readonly username: string;
  readonly password: string;
}

// Ответ POST /bot-account — без пароля. management_mode зеркалит
// backend RepoHostManagementMode; has_password=true → привязка состоялась.
export interface BotAccountSummary {
  readonly username: string;
  readonly management_mode: string;
  readonly has_password: boolean;
}

// Базу строим отдельно: bot-account живёт под /connections/forgejo, не
// под /tokens (как и disconnect/decommission).
const BOT_ACCOUNT_BASE = "/api/v1/connections/forgejo/bot-account";

/**
 * Привязать существующего бота хоста по Basic Auth (логин + пароль).
 * После успеха backend резолвит этого бота как connection-credential,
 * и `can-issue` для ролей начинает возвращать `can_issue=true` →
 * становится доступен авто-выпуск per-role токенов без site-admin.
 *
 * Коды ошибок (различаются по `HttpError.status`): 422 — логин не по
 * форме (`ForgejoRequestError.detail` равен
 * `FORGEJO_INVALID_BOT_USERNAME_DETAIL` из `forgejoErrors.ts`) либо
 * неверные креды бота / хост недоступен; 409 — логин занят tombstone'ом
 * забытого бота;
 * 403 — host вне allowlist; 404 — нет подключения. Пароль передаётся
 * ТОЛЬКО в JSON body (инвариант: не в URL/localStorage/логах).
 */
export async function bindBotAccount(
  body: BindBotAccountRequest,
): Promise<BotAccountSummary> {
  const response = await authedFetch(BOT_ACCOUNT_BASE, {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ForgejoRequestError(
      response.status,
      `bindBotAccount: HTTP ${String(response.status)}`,
      await readErrorDetail(response),
    );
  }
  return (await response.json()) as BotAccountSummary;
}
